/* eslint-disable react/no-unused-prop-types */
import React, { KeyboardEvent, ReactNode, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { theme } from '../../../theme/theme'
import { getUserFullName, getUserInitials } from '../../../helpers/user'
import { AvatarImage } from './AvatarImage'
import { IUserModel } from '../../../interfaces/models/users.interfaces'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import { mediaQueries } from '../../../api/media'
import { IMediaModel } from '../../../interfaces/models/media.interfaces'

interface AvatarBaseProps {
  onClick?: () => void
  size?: number
  alt?: string
  className?: string
  avatarClassName?: string
  initials?: string
  placeholder?: 'avatar' | 'society' | 'booking' | 'interests'
}
interface AvatarInnerProps extends AvatarBaseProps {
  media?: IMediaModel | undefined
  url?: string
}

export const AvatarInner = ({
  media,
  alt = 'avatar',
  size = 40,
  onClick,
  className,
  avatarClassName,
  url,
  initials,
  placeholder = 'avatar',
}: AvatarInnerProps): React.ReactNode => {
  const handleKeyDown = (e: KeyboardEvent<HTMLImageElement>): void => {
    if (e.key === 'Enter' && onClick) {
      onClick()
    }
  }

  const getBackgroundColor = (): string => {
    switch (placeholder) {
      case 'interests':
        return 'transparent'

      case 'avatar':
        if (!initials) {
          return 'white'
        }
        return theme.colors.almostBrandGreen

      default:
        return theme.colors.almostBrandGreen
    }
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full text-white
        ${onClick ? 'cursor-pointer hover:brightness-90' : 'cursor-default'}
        ${className ?? ''}
      `}
      style={{
        backgroundColor: getBackgroundColor(),
        height: size,
        width: size,
        maxWidth: size,
      }}
      role="button"
      onClick={onClick}
      tabIndex={onClick ? 0 : -1}
      onKeyDown={handleKeyDown}
    >
      <AvatarImage
        alt={alt}
        url={media?.url || url}
        initials={initials}
        placeholder={placeholder}
        size={size}
        avatarClassName={avatarClassName}
      />
    </div>
  )
}

interface AvatarProps extends AvatarBaseProps {
  mediaId: string | null | undefined
}

export const Avatar = ({
  mediaId,
  alt,
  onClick,
  size,
  className,
  avatarClassName,
  initials,
  placeholder = 'avatar',
}: AvatarProps): JSX.Element | null => {
  const { data: media } = useQuery(mediaQueries.getMedia(mediaId ?? undefined))

  return (
    <AvatarInner
      media={media}
      alt={alt}
      onClick={onClick}
      size={size}
      className={className}
      avatarClassName={avatarClassName}
      initials={initials}
      placeholder={placeholder}
    />
  )
}

interface UserAvatarProps extends AvatarBaseProps {
  user?: IUserModel
  avatarClassName?: string
}

export const UserAvatar = ({
  user,
  onClick,
  size,
  avatarClassName,
  className,
}: UserAvatarProps): ReactNode => {
  const initials = useMemo(() => {
    return getUserInitials(user)
  }, [user])
  const fullName = getUserFullName(user)
  return (
    <Avatar
      alt={`${fullName} avatar`}
      mediaId={user?.avatarId}
      onClick={onClick}
      size={size}
      className={className}
      avatarClassName={avatarClassName}
      initials={initials}
    />
  )
}

interface TicketAvatarProps extends AvatarBaseProps {
  user: { name?: string; surname?: string; avatarUrl?: string }
  url?: string
  avatarClassName?: string
}
export const TicketAvatar = ({
  user,
  url,
  onClick,
  size,
  avatarClassName,
  className,
}: TicketAvatarProps): ReactNode => {
  const initials = useMemo(() => {
    return getUserInitials(user)
  }, [user])
  const fullName = getUserFullName(user)
  return (
    <AvatarInner
      url={url || user.avatarUrl}
      alt={`${fullName} avatar`}
      onClick={onClick}
      size={size}
      className={className}
      avatarClassName={avatarClassName}
      initials={initials}
      placeholder="avatar"
    />
  )
}
interface SocietyAvatarProps extends AvatarBaseProps {
  society?: ISocietiesModel | null
}

export const SocietyAvatar = ({
  society,
  onClick,
  size,
}: SocietyAvatarProps): JSX.Element => {
  return (
    <Avatar
      placeholder="society"
      mediaId={society?.coverPhotoId}
      onClick={onClick}
      size={size}
    />
  )
}

interface SocietyAvatarWithMediaIdProps extends AvatarBaseProps {
  mediaId?: string | null
}

export const SocietyAvatarWithMediaId = ({
  mediaId,
  onClick,
  size,
}: SocietyAvatarWithMediaIdProps): JSX.Element => {
  return (
    <Avatar
      placeholder="society"
      mediaId={mediaId}
      onClick={onClick}
      size={size}
    />
  )
}

interface NestedAvatarProps extends AvatarBaseProps {
  primaryAvatar: JSX.Element
  secondaryAvatar?: JSX.Element
  avatarSize: 'sm' | 'md'
}

export const NestedAvatar = ({
  primaryAvatar,
  secondaryAvatar,
  onClick,
  avatarSize,
}: NestedAvatarProps): JSX.Element => {
  const handleKeyDown = (e: KeyboardEvent<HTMLImageElement>): void => {
    if (e.key === 'Enter' && onClick) {
      onClick()
    }
  }

  return (
    <div
      className={`relative ${
        onClick ? 'cursor-pointer hover:brightness-90' : ''
      }`}
      role="presentation"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {primaryAvatar}
      <div
        className={`absolute rounded-full border border-white
            ${avatarSize === 'md' ? 'top-5 left-4' : 'top-3 left-3'}`}
        style={{ borderColor: theme.colors.brandLightGreen }}
      >
        {secondaryAvatar}
      </div>
    </div>
  )
}
